import { ITEM_SCENE_SCALE } from '@constants'
import type { AnyID, LinkedItem } from '@types'
import { getVisibleMeshBounds } from '@utils/sceneObjects/items/newGetDataFromGLB'
import {
  isKubeBase,
  isKubeCushion,
  isKubeModule,
  isKubeRespaldo,
} from '@utils/sceneObjects/items/rinconeroUtils'
import { buildItemOBBFromMatrix } from '@utils/sceneObjects/items/itemObbUtils'
import { Matrix4, Quaternion, Vector3 } from 'three'
import { OBB } from 'three/examples/jsm/math/OBB'

/**
 * Offset máximo cara inferior vs BASE (mm).
 * Como el alto de la BA define la Y del cojín, este es el único solape vertical.
 */
export const CUSHION_SEAT_SINK_MM = 50

/**
 * Acercamiento del cojín (mm) en X/Z: la caja es mayor que la espuma redondeada.
 * Con rotación −90° el ancho del cojín mira al respaldo → hace falta nest generoso.
 */
export const CUSHION_SIDE_NEST_MM = 45

/**
 * Inset del sólido del RESPALDO (mm): OBB del RB suele sobresalir del mesh visible
 * (p.ej. K1RB 070 040 051). Permite pegar el cojín sin “congelar” el arrastre.
 */
export const CUSHION_BACKREST_INSET_MM = 40

/** Holgura de contacto (mm). */
export const CUSHION_CONTACT_TOLERANCE_MM = 1

/** Alias. */
export const CUSHION_FACE_OFFSET_MM = CUSHION_SEAT_SINK_MM
/** @deprecated */
export const CUSHION_VERTICAL_NEST_MM = 0

const neighborOBB = new OBB()
const cushionCore = new OBB()
const neighborCore = new OBB()
const footprintA = new OBB()
const footprintB = new OBB()
const _pos = new Vector3()
const _quat = new Quaternion()
const _scale = new Vector3()
const _origin = new Vector3()

type DimItem = Pick<LinkedItem, 'width' | 'height' | 'thickness' | 'matrix' | 'catalogID' | 'type'>
type CushionItem = Pick<LinkedItem, 'width' | 'height' | 'thickness'> & {
  glbSize?: LinkedItem['glbSize']
  meshLocalMin?: LinkedItem['meshLocalMin']
  meshLocalYMin?: number
  y?: number
}

function isSolidUnit(other: LinkedItem | undefined, selfId: string): boolean {
  if (!other?.matrix) return false
  if (String(other.id) === selfId) return false
  if (other.visible === false) return false
  const t = String(other.type)
  if (t === 'linkedSon') return false
  return t === 'linkedItem' || t === 'items'
}

/**
 * Medidas de colisión del cojín.
 * El AABB del GLB a veces infla el fondo por meshLocalMin.z negativo (ej. thickness 279
 * con glb.z 25mm×escala). Usamos el fondo proporcional al morph real.
 */
function cushionDims(item: CushionItem): { width: number; height: number; thickness: number } {
  const sw = Math.max(1, Number(item.width) || 1)
  const sh = Math.max(1, Number(item.height) || 1)
  const st = Math.max(1, Number(item.thickness) || 1)
  const glb = item.glbSize as Vector3 | undefined
  if (glb && glb.x > 0 && glb.y > 0 && glb.z > 0) {
    const gw = glb.x * ITEM_SCENE_SCALE
    const gt = glb.z * ITEM_SCENE_SCALE
    const sx = sw / Math.max(gw, 1e-6)
    // Fondo real del morph (evita AABB 279mm con glb.z 25mm).
    const foamT = Math.max(1, gt * sx)
    return { width: sw, height: sh, thickness: Math.min(st, foamT) }
  }
  return { width: sw, height: sh, thickness: st }
}

function footprintsOverlapXZ(
  aMatrix: Matrix4,
  a: { width: number; height: number; thickness: number },
  bMatrix: Matrix4,
  b: { width: number; height: number; thickness: number },
): boolean {
  buildItemOBBFromMatrix(aMatrix, a.width, a.height, a.thickness, footprintA)
  buildItemOBBFromMatrix(bMatrix, b.width, b.height, b.thickness, footprintB)
  footprintA.halfSize.y = 1e6
  footprintB.halfSize.y = 1e6
  return footprintA.intersectsOBB(footprintB)
}

function shrinkUniform(obb: OBB, tolMm: number, target: OBB): OBB {
  target.center.copy(obb.center)
  target.rotation.copy(obb.rotation)
  const t = Math.max(0, tolMm)
  target.halfSize.set(
    Math.max(1, obb.halfSize.x - t),
    Math.max(1, obb.halfSize.y - t),
    Math.max(1, obb.halfSize.z - t),
  )
  return target
}

type MeshLocalMin = { x: number; y: number; z: number }

/** Caché por catalogID — no mutar items del store (Immer/frozen). */
const meshLocalMinCache = new Map<string, MeshLocalMin>()

function isUsefulMin(m: MeshLocalMin | undefined | null): m is MeshLocalMin {
  return Boolean(
    m &&
    Number.isFinite(m.x) &&
    Number.isFinite(m.y) &&
    Number.isFinite(m.z) &&
    (Math.abs(m.x) > 1e-8 || Math.abs(m.y) > 1e-8 || Math.abs(m.z) > 1e-8),
  )
}

function readMeshLocalMin(item: {
  catalogID?: unknown
  id?: unknown
  meshLocalMin?: MeshLocalMin
  meshLocalYMin?: number
}): MeshLocalMin {
  const catalogKey = String(item.catalogID ?? '')
  const cached = catalogKey ? meshLocalMinCache.get(catalogKey) : undefined
  if (isUsefulMin(cached)) return cached

  const m = item.meshLocalMin
  if (isUsefulMin(m)) {
    if (catalogKey) meshLocalMinCache.set(catalogKey, m)
    return { x: m.x, y: m.y, z: m.z }
  }
  if (m && Number.isFinite(m.x) && Number.isFinite(m.y) && Number.isFinite(m.z)) {
    return { x: m.x, y: m.y, z: m.z }
  }
  const y = Number(item.meshLocalYMin)
  return { x: 0, y: Number.isFinite(y) ? y : 0, z: 0 }
}

/**
 * Precarga meshLocalMin desde GLB en una caché local (sin mutar el store).
 */
export function hydrateKubeMeshLocalMins(
  items: Map<string, LinkedItem>,
  sceneGLB: Map<string, any> | Map<any, any>,
): void {
  for (const item of items.values()) {
    if (!item || !isKubeModule(item.catalogID)) continue
    const catalogKey = String(item.catalogID)
    if (isUsefulMin(meshLocalMinCache.get(catalogKey))) continue
    if (isUsefulMin(item.meshLocalMin)) {
      meshLocalMinCache.set(catalogKey, {
        x: item.meshLocalMin!.x,
        y: item.meshLocalMin!.y,
        z: item.meshLocalMin!.z,
      })
      continue
    }

    const entry = sceneGLB.get(item.catalogID as any) as { glb?: any } | undefined
    const root = entry?.glb?.scene ?? entry?.glb
    const bounds = getVisibleMeshBounds(root)
    if (!bounds) continue
    meshLocalMinCache.set(catalogKey, {
      x: bounds.min.x,
      y: bounds.min.y,
      z: bounds.min.z,
    })
  }
}

/**
 * OBB alineada al mesh: origen + meshLocalMin×scale, tamaño = ancho/alto/fondo de la unidad.
 * Igual criterio que usar el alto de la BA para la Y del cojín.
 */
function buildUnitSolidOBB(
  matrix: Matrix4,
  width: number,
  height: number,
  thickness: number,
  meshLocalMin: { x: number; y: number; z: number },
  settled: boolean,
  target: OBB,
): OBB {
  if (settled) {
    return buildItemOBBFromMatrix(matrix, width, height, thickness, target)
  }
  matrix.decompose(_pos, _quat, _scale)
  _origin
    .set(
      meshLocalMin.x * ITEM_SCENE_SCALE,
      meshLocalMin.y * ITEM_SCENE_SCALE,
      meshLocalMin.z * ITEM_SCENE_SCALE,
    )
    .applyQuaternion(_quat)
    .add(_pos)
  const aligned = new Matrix4().compose(_origin, _quat, _scale)
  return buildItemOBBFromMatrix(aligned, width, height, thickness, target)
}

/**
 * OBB del cojín tras asentar mesh en XYZ.
 * @param sideNest — solo contra BA/RB (acercar al respaldo); contra otros cojines = 0.
 */
function buildCushionCollisionOBB(
  matrix: Matrix4,
  item: CushionItem,
  target: OBB,
  sideNest = CUSHION_SIDE_NEST_MM,
): OBB {
  const dims = cushionDims(item)
  buildItemOBBFromMatrix(matrix, dims.width, dims.height, dims.thickness, target)
  const nest = Math.max(0, sideNest)
  if (nest > 0) {
    target.halfSize.x = Math.max(1, target.halfSize.x - nest)
    target.halfSize.z = Math.max(1, target.halfSize.z - nest)
  }
  return target
}

/**
 * Sólido de otra unidad con sus medidas reales (ancho × alto × fondo).
 * - BASE: alto − SEAT_SINK
 * - COJÍN: asentado en XYZ en render → OBB desde origen de matriz (settled)
 * - RB / resto: OBB alineada con meshLocalMin
 */
function buildNeighborSolidForCushion(other: LinkedItem, target: OBB): OBB {
  const w = Math.max(1, other.width || 1)
  const h = Math.max(1, other.height || 1)
  const t = Math.max(1, other.thickness || 1)
  const min = readMeshLocalMin(other)

  if (isKubeBase(other.catalogID)) {
    const sink = Math.min(CUSHION_SEAT_SINK_MM, Math.max(0, h - 2))
    buildUnitSolidOBB(other.matrix as Matrix4, w, Math.max(1, h - sink), t, min, false, target)
  } else if (isKubeCushion(other.catalogID)) {
    // Mesh asentado → OBB desde origen; dims de espuma (no AABB inflado).
    const cd = cushionDims(other)
    buildUnitSolidOBB(other.matrix as Matrix4, cd.width, cd.height, cd.thickness, min, true, target)
  } else {
    buildUnitSolidOBB(other.matrix as Matrix4, w, h, t, min, false, target)
    // Respaldo: contraer un poco el sólido (OBB > mesh en varios K1RB).
    if (isKubeRespaldo(other.catalogID)) {
      const inset = CUSHION_BACKREST_INSET_MM
      target.halfSize.x = Math.max(1, target.halfSize.x - inset)
      target.halfSize.y = Math.max(1, target.halfSize.y - inset)
      target.halfSize.z = Math.max(1, target.halfSize.z - inset)
    }
  }
  return target
}

export function findCushionSupportSeatY(
  cushionMatrix: Matrix4,
  cushion: CushionItem,
  items: Map<string, LinkedItem>,
  excludeId?: AnyID,
): number {
  const selfId = excludeId != null ? String(excludeId) : ''
  const dims = cushionDims(cushion)
  let bestTop: number | null = null

  for (const [oid, other] of items) {
    if (selfId && String(oid) === selfId) continue
    if (!isSolidUnit(other, selfId)) continue
    if (!isKubeBase(other.catalogID)) continue
    if (!footprintsOverlapXZ(cushionMatrix, dims, other.matrix as Matrix4, other)) continue

    ;(other.matrix as Matrix4).decompose(_pos, _quat, _scale)
    const top = _pos.y + (other.height || 0)
    if (bestTop === null || top > bestTop) bestTop = top
  }

  return bestTop === null ? 0 : Math.max(0, bestTop - CUSHION_SEAT_SINK_MM)
}

/**
 * True si el cojín invade el núcleo de otra unidad más allá del offset permitido.
 * Comprueba BB orientada (matriz local→global) de BA, RB, cojines y resto.
 */
export function findCushionVolumePenetration(
  itemId: AnyID,
  matrix: Matrix4,
  item: CushionItem,
  items: Map<string, LinkedItem>,
  candidates: Iterable<AnyID>,
  excludeIds: Iterable<AnyID> = [],
  toleranceMm = CUSHION_CONTACT_TOLERANCE_MM,
): AnyID | null {
  const exclude = new Set(Array.from(excludeIds, (oid) => String(oid)))
  exclude.add(String(itemId))

  for (const otherId of candidates) {
    if (exclude.has(String(otherId))) continue
    const other = items.get(String(otherId)) as LinkedItem | undefined
    if (!isSolidUnit(other, String(itemId))) continue

    // Nest lateral solo vs BA/RB; vs otro cojín caja completa (si no, el 3º cabe “dentro” del 2º).
    const sideNest = isKubeCushion(other.catalogID) ? 0 : CUSHION_SIDE_NEST_MM
    buildCushionCollisionOBB(matrix, item, cushionCore, sideNest)
    if (toleranceMm > 0) {
      cushionCore.halfSize.x = Math.max(1, cushionCore.halfSize.x - toleranceMm)
      cushionCore.halfSize.y = Math.max(1, cushionCore.halfSize.y - toleranceMm)
      cushionCore.halfSize.z = Math.max(1, cushionCore.halfSize.z - toleranceMm)
    }

    buildNeighborSolidForCushion(other!, neighborOBB)
    shrinkUniform(neighborOBB, toleranceMm, neighborCore)

    if (cushionCore.intersectsOBB(neighborCore)) {
      return otherId as AnyID
    }
  }
  return null
}

function composePose(x: number, y: number, z: number, rot: Quaternion, scale: Vector3): Matrix4 {
  return new Matrix4().compose(new Vector3(x, y, z), rot, scale)
}

/**
 * Arrastre libre sobre base / hacia respaldos:
 * 1) pose completa  2) segmento last→proposed  3) deslizar un eje (evita “congelado”).
 */
export function resolveCushionFreeDragPosition(
  itemId: AnyID,
  lastMatrix: Matrix4,
  proposedMatrix: Matrix4,
  item: CushionItem,
  items: Map<string, LinkedItem>,
  candidates: Iterable<AnyID>,
  excludeIds: Iterable<AnyID> = [],
): Matrix4 | null {
  lastMatrix.decompose(_pos, _quat, _scale)
  const lastPos = _pos.clone()
  const lastRot = _quat.clone()
  const lastScale = _scale.clone()

  const proposedPos = new Vector3()
  proposedMatrix.decompose(proposedPos, new Quaternion(), new Vector3())
  proposedPos.y = Math.max(0, proposedPos.y)

  const isFree = (pos: Vector3): Matrix4 | null => {
    const probe = composePose(pos.x, Math.max(0, pos.y), pos.z, lastRot, lastScale)
    const seatY = findCushionSupportSeatY(probe, item, items, itemId)
    const y = Math.max(seatY, Math.max(0, pos.y))
    const candidate = composePose(pos.x, y, pos.z, lastRot, lastScale)
    if (findCushionVolumePenetration(itemId, candidate, item, items, candidates, excludeIds)) {
      return null
    }
    return candidate
  }

  const direct = isFree(proposedPos)
  if (direct) return direct

  let lastFree = isFree(lastPos)
  if (!lastFree) {
    const stuckMat = composePose(lastPos.x, lastPos.y, lastPos.z, lastRot, lastScale)
    const hit = findCushionVolumePenetration(itemId, stuckMat, item, items, candidates, excludeIds)
    if (hit) {
      const unstick = depenetrateCushionPose(itemId, lastPos, lastRot, item, items, candidates, hit)
      lastFree = isFree(unstick)
    }
    // Nunca congelar el arrastre: conservar última matriz si no hay salida limpia.
    if (!lastFree) return lastMatrix.clone()
  }

  let lo = 0
  let hi = 1
  let best = lastFree
  for (let i = 0; i < 16; i++) {
    const mid = (lo + hi) * 0.5
    const sample = new Vector3().lerpVectors(lastPos, proposedPos, mid)
    const free = isFree(sample)
    if (free) {
      lo = mid
      best = free
    } else {
      hi = mid
    }
  }

  // Deslizamiento en ejes: pegado al RB pero móvil en paralelo (sin tropezones multi-eje).
  const bestPos = new Vector3()
  best.decompose(bestPos, new Quaternion(), new Vector3())
  let bestScore = bestPos.distanceToSquared(proposedPos)

  const axisTries = [
    new Vector3(proposedPos.x, lastPos.y, lastPos.z),
    new Vector3(lastPos.x, lastPos.y, proposedPos.z),
    new Vector3(lastPos.x, proposedPos.y, lastPos.z),
    new Vector3(proposedPos.x, lastPos.y, proposedPos.z),
  ]
  for (const tryPos of axisTries) {
    const free = isFree(tryPos)
    if (!free) continue
    const p = new Vector3()
    free.decompose(p, new Quaternion(), new Vector3())
    const score = p.distanceToSquared(proposedPos)
    if (score < bestScore) {
      bestScore = score
      best = free
    }
  }

  return best
}

/** Empuja fuera del interior (XZ + Y) hasta pose libre — usado al dibujar. */
export function depenetrateCushionPose(
  itemId: AnyID,
  position: Vector3,
  quaternion: Quaternion,
  item: CushionItem,
  items: Map<string, LinkedItem>,
  candidates: Iterable<AnyID>,
  hitId: AnyID,
): Vector3 {
  const pos = position.clone()
  const scale = new Vector3(1, 1, 1)
  const other = items.get(String(hitId)) as LinkedItem | undefined
  if (!other?.matrix) return pos

  const otherPos = new Vector3()
  ;(other.matrix as Matrix4).decompose(otherPos, new Quaternion(), new Vector3())
  const away = new Vector3(pos.x - otherPos.x, 0, pos.z - otherPos.z)
  if (away.lengthSq() < 1e-6) away.set(1, 0, 0)
  away.normalize()

  for (let step = 0; step < 160; step++) {
    const mat = composePose(pos.x, Math.max(0, pos.y), pos.z, quaternion, scale)
    const seatY = findCushionSupportSeatY(mat, item, items, itemId)
    pos.y = Math.max(seatY, Math.max(0, pos.y))
    const candidate = composePose(pos.x, pos.y, pos.z, quaternion, scale)
    const hit = findCushionVolumePenetration(itemId, candidate, item, items, candidates)
    if (!hit) return pos

    if (step % 2 === 0) pos.addScaledVector(away, 6)
    else pos.y += 6

    if (step === 80) {
      pos.x = position.x
      pos.z = position.z
      away.negate()
    }
  }
  return pos
}

export function resolveKubeCushionStackY(
  cushionMatrix: Matrix4,
  cushion: DimItem & CushionItem,
  items: Map<string, LinkedItem>,
  excludeId?: AnyID,
): number {
  return findCushionSupportSeatY(cushionMatrix, cushion, items, excludeId)
}

export function computeKubeCushionOnBasePose(
  cushion: CushionItem,
  base: LinkedItem,
): { position: Vector3; quaternion: Quaternion } | null {
  if (!base?.matrix || !isKubeBase(base.catalogID)) return null

  const dims = cushionDims(cushion)
  const basePos = new Vector3()
  const baseQuat = new Quaternion()
  ;(base.matrix as Matrix4).decompose(basePos, baseQuat, new Vector3())

  const baseCenterOffset = new Vector3(
    (base.width || 0) / 2,
    0,
    (base.thickness || 0) / 2,
  ).applyQuaternion(baseQuat)
  const cushionOriginOffset = new Vector3(dims.width / 2, 0, dims.thickness / 2).applyQuaternion(
    baseQuat,
  )

  const position = basePos.clone().add(baseCenterOffset).sub(cushionOriginOffset)
  // Máximo permitido hacia abajo = offset de asiento (no más).
  position.y = Math.max(0, basePos.y + (base.height || 0) - CUSHION_SEAT_SINK_MM)

  return { position, quaternion: baseQuat.clone() }
}

/**
 * Colocación al dibujar: sobre la base si hay, pero SIEMPRE al lado de otros cojines
 * (nunca apilados ni uno dentro de otro).
 */
export function resolveKubeCushionDrawPose(
  itemId: AnyID,
  cushion: CushionItem & { id?: AnyID; catalogID?: unknown },
  items: Map<string, LinkedItem>,
  base: LinkedItem | null,
): { position: Vector3; quaternion: Quaternion } {
  const dims = cushionDims(cushion)
  const step = Math.max(dims.width, 1) + 8

  let position = new Vector3(1000, cushion.y ?? 0, 0)
  let quaternion = new Quaternion().setFromAxisAngle(new Vector3(0, 1, 0), Math.PI)

  if (base) {
    const stacked = computeKubeCushionOnBasePose(cushion, base)
    if (stacked) {
      position.copy(stacked.position)
      quaternion.copy(stacked.quaternion)
    }
  }

  const candidates = collectCushionCollisionCandidates(items, itemId)
  const xAxis = new Vector3(1, 0, 0).applyQuaternion(quaternion)
  const seatY = position.y

  const isFreeAt = (pos: Vector3) => {
    const mat = composePose(pos.x, pos.y, pos.z, quaternion, new Vector3(1, 1, 1))
    return !findCushionVolumePenetration(itemId, mat, cushion, items, candidates)
  }

  // Anclar a la derecha del cojín más alejado en +X local (fila estable 1º, 2º, 3º…).
  let maxProj = -Infinity
  let hasOtherCushion = false
  for (const oid of candidates) {
    const other = items.get(String(oid)) as LinkedItem | undefined
    if (!other?.matrix || !isKubeCushion(other.catalogID)) continue
    hasOtherCushion = true
    const op = new Vector3()
    ;(other.matrix as Matrix4).decompose(op, new Quaternion(), new Vector3())
    const otherDims = cushionDims(other)
    const proj = op.clone().sub(position).dot(xAxis)
    const rightEdge = proj + otherDims.width
    if (rightEdge > maxProj) maxProj = rightEdge
  }

  if (hasOtherCushion && Number.isFinite(maxProj)) {
    const beside = position.clone().addScaledVector(xAxis, maxProj + 4)
    beside.y = seatY
    if (isFreeAt(beside)) {
      return { position: beside, quaternion }
    }
    // Si choca (p.ej. con RB), seguir desplazando en +X.
    for (let n = 1; n <= 20; n++) {
      const trial = beside.clone().addScaledVector(xAxis, step * n)
      trial.y = seatY
      if (isFreeAt(trial)) return { position: trial, quaternion }
    }
  }

  if (isFreeAt(position)) {
    return { position, quaternion }
  }

  const dirs = [
    xAxis,
    xAxis.clone().negate(),
    new Vector3(0, 0, 1).applyQuaternion(quaternion),
    new Vector3(0, 0, -1).applyQuaternion(quaternion),
  ]
  for (const dir of dirs) {
    for (let n = 1; n <= 24; n++) {
      const trial = position.clone().addScaledVector(dir, step * n)
      trial.y = seatY
      if (isFreeAt(trial)) return { position: trial, quaternion }
    }
  }

  position.y = seatY
  return { position, quaternion }
}

export function involvesKubeCushion(aCatalog: unknown, bCatalog: unknown): boolean {
  return isKubeCushion(aCatalog) || isKubeCushion(bCatalog)
}

export function collectCushionCollisionCandidates(
  items: Map<string, LinkedItem>,
  selfId: AnyID,
): AnyID[] {
  const self = String(selfId)
  return Array.from(items.keys()).filter((oid) =>
    isSolidUnit(items.get(String(oid)) as LinkedItem | undefined, self),
  ) as AnyID[]
}
